import React, { useContext, useEffect, useMemo, useState } from "react"
import { View, ScrollView, StyleSheet, Text, TouchableOpacity } from "react-native"
import moment from "moment"
import "moment/locale/fr"
import * as amplitude from "@amplitude/analytics-react-native"
import FilledButtonM from "@common/components/button/FilledButtonM"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import BrevetFunnelHeader from "./components/BrevetFunnelHeader"
import BrevetWeekendTimeline from "./components/BrevetWeekendTimeline"
import { useBrevetFunnel } from "./context/BrevetFunnelContext"
import { recommendFormula } from "./utils/recommendFormula"
import { BREVET_FORMULAS, resolveFormula } from "./constants/brevetFormulas"
import {
    BREVET_SUBJECTS,
    BREVET_MODULES_BY_SUBJECT,
} from "./constants/brevetModules"
import {
    getBookableSlots,
    getFormulaEconomics,
    isFunnelClosed,
} from "./constants/brevetSlots"
import { BREVET_2026_DATE } from "./utils/countdownToBrevet"

moment.locale("fr")

const SUBJECT_EMOJI = {
    maths: "📐",
    francais: "📖",
    histoire_geo: "🗺️",
    sciences: "🔬",
    oral: "🎤",
}

const TIME_LABEL = {
    "1h": "~1h / week-end",
    "2_3h": "2-3h / week-end",
    "4h_plus": "4h+ / week-end",
}

/**
 * Build the per-weekend stations array, with two key properties:
 *  - subjects are scheduled in CONTIGUOUS BLOCKS (not interleaved), so a parent
 *    seeing math + français on 8h reads m / m / f / brevet blanc instead of
 *    m / f / m / brevet blanc — easier mental model + clearer pedagogical flow.
 *  - last weekend (≥3 WE only) is "Brevet blanc & révision" (📋).
 *
 * Returns an array of N stations (one per weekend) + the final "Brevet" station.
 */
const buildStations = ({ subjectIds, modules, formula, startDateISO }) => {
    const start = moment(startDateISO)
    const nbWeekends = formula.weekends

    // Group modules by subject, preserving the order from BREVET_MODULES_BY_SUBJECT.
    const modulesBySubject = {}
    for (const m of modules) {
        if (!modulesBySubject[m.subjectId]) modulesBySubject[m.subjectId] = []
        modulesBySubject[m.subjectId].push(m)
    }
    // Keep only subjects the user picked AND that have at least 1 priority module
    const subjectsWithModules = subjectIds.filter(
        (sid) => modulesBySubject[sid]?.length > 0
    )
    const distinctCount = subjectsWithModules.length || 1

    // Track which module ids have been used as a principal so we don't repeat.
    const usedAsPrincipal = new Set()

    // "Brevet blanc & révision" eats one WE of programme. Only worth the
    // tradeoff when the formula has enough WEs of content to leave room for
    // it — Sprint (4 WE) and Intensif (4 WE) keep the consolidation WE,
    // Découverte (2 WE) uses both WEs for content so we don't leave a matière
    // uncovered with only 4h total.
    const reserveBrevetBlancWE = nbWeekends >= 3
    const contentWeekends = reserveBrevetBlancWE ? nbWeekends - 1 : nbWeekends

    // Fair-share allocation: floor(N/M) per subject, +1 for the first R subjects
    // (R = N % M). E.g. 3 content WEs × 2 subjects → [2, 1] (math gets 2, fr gets 1).
    const baseAlloc = Math.floor(contentWeekends / distinctCount)
    const remainder = contentWeekends % distinctCount
    const allocPerSubject = subjectsWithModules.map((_, i) =>
        baseAlloc + (i < remainder ? 1 : 0)
    )

    // Build content weekends as contiguous per-subject blocks.
    const weekStations = []
    let weekIdx = 0
    for (let s = 0; s < subjectsWithModules.length && weekIdx < contentWeekends; s++) {
        const subjectId = subjectsWithModules[s]
        const candidates = modulesBySubject[subjectId] || []
        for (let k = 0; k < allocPerSubject[s] && weekIdx < contentWeekends; k++) {
            const principal =
                candidates.find((m) => !usedAsPrincipal.has(m.id)) ||
                candidates[k] ||
                candidates[0]
            if (principal) usedAsPrincipal.add(principal.id)

            const emoji = principal
                ? SUBJECT_EMOJI[principal.subjectId] || "🎯"
                : "🎯"
            const date = start.clone().add(weekIdx, "weeks")
            weekStations.push({
                id: `w${weekIdx + 1}`,
                weekLabel: `Week-end ${weekIdx + 1}`,
                subLabel: date.format("ddd D MMM"),
                emoji,
                moduleShort: principal?.label || "À définir",
                moduleDetails: principal ? [principal] : [],
            })
            weekIdx++
        }
    }

    // "Brevet blanc & révision" final content WE (Sprint / Intensif only).
    if (reserveBrevetBlancWE) {
        const idx = nbWeekends - 1
        const date = start.clone().add(idx, "weeks")
        // Secondary modules = anything not used as a principal so far (revision recap).
        const secondaryModules = modules.filter(
            (m) => !usedAsPrincipal.has(m.id)
        )
        weekStations.push({
            id: `w${nbWeekends}`,
            weekLabel: `Week-end ${nbWeekends}`,
            subLabel: date.format("ddd D MMM"),
            emoji: "📋",
            moduleShort: "Brevet blanc & révision",
            moduleDetails: secondaryModules.length
                ? secondaryModules
                : [{ id: "revision_fundamentals", label: "Revue des fondamentaux" }],
        })
    }

    // Final "Brevet" station (always tappable=false).
    const brevet = moment(BREVET_2026_DATE)
    weekStations.push({
        id: "brevet",
        weekLabel: "Brevet",
        subLabel: brevet.format("ddd D MMM"),
        emoji: "🏁",
        moduleShort: "Jour J",
        moduleDetails: [],
        isFinal: true,
    })

    return weekStations
}

const BrevetRecommendationScreen = ({ navigation }) => {
    const dimensions = useContext(DimensionsContext)
    const { state, update } = useBrevetFunnel()

    const containerWidth = Math.min(dimensions?.width || 400, 460)

    // Funnel fermé (Phase 3) : pas de phase active → `getBookableSlots()` vide,
    // `stationsStartDateISO` null, `moment(null)` rendait "Invalid date" sur
    // chaque station. Calcul placé en haut, early-return après les hooks pour
    // respecter les rules-of-hooks (cf. BrevetPricingCheckoutScreen).
    const funnelClosed = useMemo(() => isFunnelClosed(), [])

    const reco = useMemo(
        () =>
            recommendFormula({
                subjects: state.subjects,
                objective: state.objective,
                brevetBlanc: state.brevetBlanc,
                niveauActuel: state.niveauActuel,
                autonomie: state.autonomie,
                timePerWeekend: state.timePerWeekend,
            }),
        [
            state.subjects,
            state.objective,
            state.brevetBlanc,
            state.niveauActuel,
            state.autonomie,
            state.timePerWeekend,
        ]
    )

    // Local state: formula being visualized in Step 3. Separate from reco.formula
    // so the user can "preview" the floor formula via the mismatch CTA without
    // committing to it yet. Committed on "Voir mes places" tap.
    const [visualFormulaId, setVisualFormulaId] = useState(reco.formula.id)

    // Keep visualFormulaId in sync if the upstream reco changes (e.g. user goes
    // back and edits Step 1/2 then returns).
    useEffect(() => {
        setVisualFormulaId(reco.formula.id)
    }, [reco.formula.id])

    // Formule visualisée résolue avec les économies de la phase active (nb WE /
    // heures cohorte-aware). Les "stations" du parcours lisent `weekends` ici.
    const visualFormula = useMemo(
        () =>
            resolveFormula(
                visualFormulaId,
                getFormulaEconomics(visualFormulaId)
            ) ||
            resolveFormula(reco.formula.id, getFormulaEconomics(reco.formula.id)) ||
            reco.formula,
        [visualFormulaId, reco.formula]
    )

    const priorityModulesForVisual = useMemo(() => {
        // If we're visualizing a formula different from the reco, recompute
        // priority modules for THAT formula. Otherwise, reuse reco.priorityModules.
        if (visualFormulaId === reco.formula.id) return reco.priorityModules
        const allModules = (state.subjects || []).flatMap((sid) =>
            (BREVET_MODULES_BY_SUBJECT[sid] || []).map((m) => ({
                ...m,
                subjectId: sid,
            }))
        )
        if (!visualFormula.maxModules) return allModules
        return allModules.slice(0, visualFormula.maxModules)
    }, [visualFormulaId, reco.formula.id, reco.priorityModules, state.subjects, visualFormula.maxModules])

    const modulesToShow = useMemo(() => {
        if (state.selectedModules?.length) {
            const flat = Object.values(BREVET_MODULES_BY_SUBJECT).flat()
            return state.selectedModules
                .map((id) => {
                    const subjectId = Object.keys(BREVET_MODULES_BY_SUBJECT).find(
                        (sid) =>
                            BREVET_MODULES_BY_SUBJECT[sid].some((m) => m.id === id)
                    )
                    const found = flat.find((m) => m.id === id)
                    return found ? { ...found, subjectId } : null
                })
                .filter(Boolean)
        }
        return priorityModulesForVisual
    }, [state.selectedModules, priorityModulesForVisual])

    // Date de référence pour la visualisation des stations : 1re session du 1er
    // slot bookable de la phase active. Si plus aucune phase n'est ouverte
    // (funnel fermé), `getBookableSlots()` est vide et on garde `null` — le
    // builder doit tolérer (cf. fallback historique aux clés ISO).
    const stationsStartDateISO = useMemo(() => {
        const slots = getBookableSlots()
        return slots[0]?.firstSessionDate || null
    }, [])

    const stations = useMemo(
        () =>
            buildStations({
                subjectIds: state.subjects || [],
                modules: modulesToShow,
                formula: visualFormula,
                startDateISO: stationsStartDateISO,
            }),
        [state.subjects, modulesToShow, visualFormula, stationsStartDateISO]
    )

    const firstname = state.studentFirstname || "votre enfant"

    // Amplitude — reco view (1x au mount).
    useEffect(() => {
        amplitude.track("brevet_reco_viewed", {
            funnel_state_id: state.funnelStateId,
            formula_id: reco.formula.id,
            mismatch: reco.mismatch,
            floor_id: reco.floor?.id,
            ceiling_id: reco.ceiling?.id,
            objective: state.objective,
            subjects: state.subjects,
            time_per_weekend: state.timePerWeekend,
            referral_code: state.referralCode || null,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Early-return après tous les hooks (rules-of-hooks). Quand le funnel est
    // fermé, les `stations` plus haut ont du `Invalid date` mais ne sont jamais
    // rendues — on affiche le notice "Sessions complètes" à la place.
    if (funnelClosed) {
        return (
            <View style={styles.root}>
                <View style={[styles.frame, { width: containerWidth }]}>
                    <BrevetFunnelHeader
                        step={3}
                        total={4}
                        onBack={() => navigation.goBack()}
                    />
                    <ScrollView
                        contentContainerStyle={styles.content}
                        showsVerticalScrollIndicator={false}
                    >
                        <View style={styles.closedNotice}>
                            <Text style={styles.closedTitle}>Sessions complètes</Text>
                            <Text style={styles.closedText}>
                                Les inscriptions au stage de révision Brevet sont closes pour cette session.
                                Pour toute demande, contactez-nous via fr.masteur.com/brevet.
                            </Text>
                        </View>
                    </ScrollView>
                </View>
            </View>
        )
    }

    const onContinue = () => {
        // Persist the formula the user is currently visualizing — that's their pick.
        update({ selectedFormulaId: visualFormulaId })
        navigation.navigate("brevet/pricing-checkout")
    }

    const onTapUpsell = () => {
        amplitude.track("brevet_reco_mismatch_cta_tapped", {
            funnel_state_id: state.funnelStateId,
            from_formula: reco.formula.id,
            to_formula: reco.floor?.id,
        })
        setVisualFormulaId(reco.floor.id)
    }

    // Mismatch upsell block visibility — only when reco surfaced a mismatch AND
    // the user hasn't already opted in to view the floor formula.
    const showMismatchBlock =
        reco.mismatch && visualFormulaId === reco.formula.id && reco.floor

    // Floor formula résolue avec les économies de la phase active — le copy
    // "N WE × Xh" doit refléter la cohorte courante, pas la grille statique.
    const floorResolved = reco.floor
        ? resolveFormula(reco.floor.id, getFormulaEconomics(reco.floor.id)) ||
          reco.floor
        : null

    // Labels for mismatch copy.
    const formattedTime = TIME_LABEL[state.timePerWeekend] || "votre temps dispo"
    const matieresCount = state.subjects?.length || 0
    const matieresLabel =
        matieresCount > 1 ? `${matieresCount} matières` : `${matieresCount} matière`
    // Priority matières = the DISTINCT subjects actually scheduled as principal in
    // the timeline (i.e. what the parent will really see covered). Derived from
    // stations to stay truthful when the formula has fewer content WEs than
    // selected matières (e.g. Découverte 2 WE × 3 matières → only 2 covered).
    const priorityMatieresLabel = (() => {
        const subjectIds = []
        for (const s of stations) {
            if (s.isFinal) continue
            const sid = s.moduleDetails?.[0]?.subjectId
            if (sid && !subjectIds.includes(sid)) subjectIds.push(sid)
        }
        const labels = subjectIds
            .map((sid) => BREVET_SUBJECTS.find((x) => x.id === sid)?.short)
            .filter(Boolean)
        return labels.length ? labels.join(" + ") : "les fondamentaux"
    })()

    return (
        <View style={styles.root}>
            <View style={[styles.frame, { width: containerWidth }]}>
                <BrevetFunnelHeader
                    step={3}
                    total={4}
                    onBack={() => navigation.goBack()}
                />
                <ScrollView
                    contentContainerStyle={styles.content}
                    showsVerticalScrollIndicator={false}
                >
                    <Text style={styles.title}>
                        Programme proposé pour {firstname}
                    </Text>

                    {/* Mismatch upsell block — placed BEFORE the parcours so it
                        frames why we've calibrated to the user's time/matières
                        before showing the timeline. */}
                    {showMismatchBlock ? (
                        <View style={styles.mismatchBox}>
                            <View style={styles.mismatchHeader}>
                                <Text style={styles.mismatchIcon}>💡</Text>
                                <Text style={styles.mismatchTitle}>
                                    On a calibré votre parcours
                                </Text>
                            </View>
                            <Text style={styles.mismatchBody}>
                                Avec {formattedTime} et {matieresLabel}, on se concentre sur l'essentiel :{" "}
                                <Text style={styles.mismatchBold}>
                                    {priorityMatieresLabel}
                                </Text>{" "}
                                — les piliers du brevet.
                            </Text>
                            <Text style={styles.mismatchBody}>
                                Vous avez plus de temps que prévu ? L'
                                <Text style={styles.mismatchBold}>
                                    {floorResolved.label}
                                </Text>{" "}
                                ({floorResolved.weekends} WE ×{" "}
                                {floorResolved.hoursPerWeekend ??
                                    Math.round(
                                        floorResolved.hours /
                                            floorResolved.weekends
                                    )}
                                h) couvre vos {matieresCount} matières.
                            </Text>
                            <TouchableOpacity
                                onPress={onTapUpsell}
                                style={styles.mismatchCta}
                                activeOpacity={0.8}
                            >
                                <Text style={styles.mismatchCtaText}>
                                    Voir l'{reco.floor.label} →
                                </Text>
                            </TouchableOpacity>
                        </View>
                    ) : null}

                    {/* Formula title — visual anchor for Step 3 */}
                    <Text style={styles.planningTitle}>
                        Le parcours {visualFormula.label} — {visualFormula.hours} h sur {visualFormula.weekends} week-end{visualFormula.weekends > 1 ? "s" : ""}
                    </Text>
                    <BrevetWeekendTimeline
                        stations={stations}
                        viewportWidth={containerWidth - 40}
                    />
                </ScrollView>
                <View style={styles.footer}>
                    <FilledButtonM
                        label="Voir mes places"
                        onPress={onContinue}
                        style={{ width: "100%", marginBottom: 0 }}
                    />
                </View>
            </View>
        </View>
    )
}

const styles = StyleSheet.create({
    root: {
        flex: 1,
        alignItems: "center",
        backgroundColor: theme.colors.whiteAlmost,
    },
    frame: {
        flex: 1,
        backgroundColor: theme.colors.white,
        paddingHorizontal: 20,
    },
    content: {
        paddingTop: 14,
        paddingBottom: 24,
    },
    title: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 22,
        color: theme.colors.primary,
        marginBottom: 14,
    },
    // Mismatch upsell block — D3.
    mismatchBox: {
        backgroundColor: theme.colors.primaryLighter,
        borderRadius: 12,
        padding: 14,
        marginBottom: 16,
        gap: 8,
    },
    mismatchHeader: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
    },
    mismatchIcon: {
        fontSize: 18,
    },
    mismatchTitle: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 14,
        color: theme.colors.primary,
    },
    mismatchBody: {
        fontFamily: "Montserrat-Regular",
        fontSize: 12,
        color: theme.colors.primary,
        lineHeight: 17,
    },
    mismatchBold: {
        fontFamily: "Montserrat-Bold",
    },
    mismatchCta: {
        alignSelf: "flex-start",
        marginTop: 4,
        paddingVertical: 8,
        paddingHorizontal: 14,
        backgroundColor: theme.colors.white,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: theme.colors.primary,
    },
    mismatchCtaText: {
        fontFamily: "Montserrat-Bold",
        fontSize: 12,
        color: theme.colors.primary,
    },
    // Bigger formula title — D4b.
    planningTitle: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 20,
        color: theme.colors.primary,
        marginBottom: 8,
    },
    footer: {
        paddingVertical: 14,
        borderTopWidth: 1,
        borderTopColor: theme.colors.lightMore,
        backgroundColor: theme.colors.white,
    },
    // Funnel fermé (Phase 3). Cohérent visuellement avec
    // BrevetPricingCheckoutScreen.closedNotice.
    closedNotice: {
        marginTop: 40,
        padding: 24,
        backgroundColor: theme.colors.primaryLighter,
        borderRadius: 12,
        alignItems: "center",
    },
    closedTitle: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: 18,
        color: theme.colors.primary,
        marginBottom: 10,
        textAlign: "center",
    },
    closedText: {
        fontFamily: "Montserrat-Medium",
        fontSize: 13,
        color: theme.colors.grey,
        lineHeight: 19,
        textAlign: "center",
    },
})

export default BrevetRecommendationScreen
